import { useState, useRef } from "react";

/**
 * SideQuestHeatmap
 * GitHub-style heatmap showing how often each side quest has been completed.
 *
 * Props:
 *   sideQuestCompletions  – { [dateKey]: { [questTitle]: true } }
 *   activeSideQuests      – [{ title, emoji }]
 *   weeks                 – number of weeks to show (default 26) — overridden by internal range picker
 */
export default function SideQuestHeatmap({
  sideQuestCompletions = {},
  activeSideQuests = [],
}) {
  // Build list of quests that have at least one completion
  const questsWithData = activeSideQuests.filter((q) =>
    Object.values(sideQuestCompletions).some((day) => day[q.title])
  );

  const [selectedIdx, setSelectedIdx] = useState(0);
  const [range, setRange] = useState(13); // 13 = 3M, 26 = 6M, 52 = 1Y
  const touchStartX = useRef(null);

  function swipe(dir) {
    if (!questsWithData.length) return;
    setSelectedIdx((prev) => (prev + dir + questsWithData.length) % questsWithData.length);
  }

  if (questsWithData.length === 0) {
    return (
      <div style={{ color: "#6b8cb0", fontSize: "13px", textAlign: "center", padding: "12px 0" }}>
        Complete some side quests to see your consistency here.
      </div>
    );
  }

  const selectedQuest = questsWithData[selectedIdx];
  const weeks = range;

  // ── build grid ──────────────────────────────────────────────────────────────
  const today = new Date();
  today.setHours(12, 0, 0, 0);

  const endDate = new Date(today);
  const endDow = endDate.getDay();
  endDate.setDate(endDate.getDate() + (6 - endDow));

  const startDate = new Date(endDate);
  startDate.setDate(startDate.getDate() - weeks * 7 + 1);
  const startDow = startDate.getDay();
  const snapBack = startDow === 0 ? 6 : startDow - 1;
  startDate.setDate(startDate.getDate() - snapBack);

  function dk(d) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }

  const columns = [];
  const cursor = new Date(startDate);
  while (cursor <= endDate) {
    const col = [];
    for (let d = 0; d < 7; d++) {
      col.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    columns.push(col);
  }

  const todayKey = dk(today);

  const COLORS = {
    future: "#f0f4f8",
    none: "#dbeafe",
    done: "#2563eb",
  };

  const LABELS = ["M", "", "W", "", "F", "", "S"];

  const monthLabels = [];
  const seenMonths = new Set();
  columns.forEach((col, ci) => {
    const firstDay = col[0];
    const monthKey = `${firstDay.getFullYear()}-${firstDay.getMonth()}`;
    if (!seenMonths.has(monthKey)) {
      seenMonths.add(monthKey);
      monthLabels.push({
        ci,
        label: firstDay.toLocaleString("default", { month: "short" }),
      });
    }
  });

  // Count total completions for selected quest within the visible range
  const cutoffKey = dk(startDate);
  const totalCompletions = Object.entries(sideQuestCompletions).filter(
    ([dateKey, day]) => dateKey >= cutoffKey && day[selectedQuest.title]
  ).length;

  return (
    <div>
      {/* Range selector */}
      <div style={{ display: "flex", gap: "6px", marginBottom: "12px" }}>
        {[
          { label: "3M", weeks: 13 },
          { label: "6M", weeks: 26 },
          { label: "1Y", weeks: 52 },
        ].map((r) => (
          <button
            key={r.label}
            onClick={() => setRange(r.weeks)}
            style={{
              padding: "4px 10px",
              borderRadius: "8px",
              border: range === r.weeks ? "2px solid #2563eb" : "2px solid #bfdbfe",
              background: range === r.weeks ? "#e0f2fe" : "#f5faff",
              color: "#1e3a5f",
              fontWeight: range === r.weeks ? "bold" : "normal",
              fontSize: "12px",
              cursor: "pointer",
            }}
          >
            {r.label}
          </button>
        ))}
      </div>

      {/* Quest selector */}
      <div
        style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}
        onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          const diff = touchStartX.current - e.changedTouches[0].clientX;
          if (Math.abs(diff) > 40) swipe(diff > 0 ? 1 : -1);
          touchStartX.current = null;
        }}
      >
        <button
          onClick={() => swipe(-1)}
          style={{ background: "#e0f2fe", border: "none", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: "#1e3a5f", fontWeight: "bold", fontSize: "16px" }}
        >‹</button>
        <select
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(Number(e.target.value))}
          style={{ flex: 1, padding: "8px 10px", borderRadius: "10px", border: "1.5px solid #bfdbfe", background: "#f5faff", color: "#1e3a5f", fontSize: "13px", fontWeight: "bold" }}
        >
          {questsWithData.map((q, i) => (
            <option key={q.title} value={i}>
              {q.emoji ? `${q.emoji} ` : ""}{q.title}
            </option>
          ))}
        </select>
        <button
          onClick={() => swipe(1)}
          style={{ background: "#e0f2fe", border: "none", borderRadius: "8px", padding: "6px 10px", cursor: "pointer", color: "#1e3a5f", fontWeight: "bold", fontSize: "16px" }}
        >›</button>
      </div>

      <div style={{ fontSize: "12px", color: "#6b8cb0", marginBottom: "8px" }}>
        Completed {totalCompletions} time{totalCompletions !== 1 ? "s" : ""} in this period
      </div>

      {/* Month labels */}
      <div style={{ display: "flex", marginBottom: "4px", paddingLeft: "18px" }}>
        {columns.map((_, ci) => {
          const ml = monthLabels.find((m) => m.ci === ci);
          return (
            <div key={ci} style={{ flex: 1, minWidth: 0 }}>
              {ml ? (
                <span style={{ fontSize: "9px", color: "#6b8cb0", fontWeight: "bold", whiteSpace: "nowrap" }}>
                  {ml.label}
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* Grid */}
      <div style={{ display: "flex", gap: "2px" }}>
        {/* Day labels */}
        <div style={{ display: "flex", flexDirection: "column", gap: "2px", marginRight: "2px" }}>
          {LABELS.map((l, i) => (
            <div key={i} style={{ width: "14px", height: "14px", fontSize: "8px", color: "#6b8cb0", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              {l}
            </div>
          ))}
        </div>

        {/* Week columns */}
        {columns.map((col, ci) => (
          <div key={ci} style={{ display: "flex", flexDirection: "column", gap: "2px", flex: 1, minWidth: 0, maxWidth: "28px" }}>
            {col.map((day) => {
              const dateKey = dk(day);
              const isFuture = dateKey > todayKey;
              const completed = !isFuture && Boolean(sideQuestCompletions[dateKey]?.[selectedQuest.title]);
              const isToday = dateKey === todayKey;

              let bg = COLORS.none;
              if (isFuture) bg = COLORS.future;
              else if (completed) bg = COLORS.done;

              return (
                <div
                  key={dateKey}
                  title={`${dateKey}${completed ? " · ✅ completed" : ""}`}
                  style={{
                    width: "100%",
                    aspectRatio: "1/1",
                    borderRadius: "3px",
                    background: bg,
                    border: isToday ? "2px solid #1d4ed8" : "none",
                    boxSizing: "border-box",
                    cursor: "default",
                    flexShrink: 0,
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "10px", justifyContent: "flex-end" }}>
        <span style={{ fontSize: "10px", color: "#6b8cb0" }}>Not done</span>
        <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: COLORS.none }} />
        <div style={{ width: "12px", height: "12px", borderRadius: "3px", background: COLORS.done }} />
        <span style={{ fontSize: "10px", color: "#6b8cb0" }}>Done</span>
      </div>

      <div style={{ fontSize: "10px", color: "#6b8cb0", textAlign: "center", marginTop: "4px" }}>
        Swipe or use arrows to switch quests
      </div>
    </div>
  );
}
